'use client';

import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from '@/components/ui/overlay/dialog';
import { Warning, ArrowLeft, CreditCard } from '@phosphor-icons/react';
import { cn } from '@/lib/utils';

export interface UnavailableItem {
  id: number;
  name?: string;
  reason: string;
}

interface UnavailableItemsDialogProps {
  open: boolean;
  items: UnavailableItem[];
  cartIsEmpty: boolean;
  belowMinimum: boolean;
  onContinue: () => void;
  onBackToCart: () => void;
}

export function UnavailableItemsDialog({
  open,
  items,
  cartIsEmpty,
  belowMinimum,
  onContinue,
  onBackToCart,
}: UnavailableItemsDialogProps) {
  const canContinue = !cartIsEmpty && !belowMinimum;

  return (
    <Dialog
      open={open}
      onOpenChange={(next) => {
        if (!next) onBackToCart();
      }}
    >
      <DialogContent showClose={false} className="max-w-md">
        <DialogHeader>
          <div className="flex items-start gap-3">
            <div className="w-11 h-11 rounded-card bg-[var(--warning-bg)] flex items-center justify-center flex-shrink-0">
              <Warning weight="fill" className="w-6 h-6 text-[var(--warning)]" />
            </div>
            <div className="flex-1 pt-0.5">
              <DialogTitle>Часть товаров недоступна</DialogTitle>
              <DialogDescription className="mt-1">
                Эти позиции временно нельзя заказать — мы убрали их из корзины.
              </DialogDescription>
            </div>
          </div>
        </DialogHeader>

        <ul className="my-4 space-y-2">
          {items.map((item) => (
            <li
              key={item.id}
              className="flex items-start justify-between gap-3 p-3 rounded-card bg-[var(--surface-secondary)] border border-[var(--border-default)]"
            >
              <span className="text-sm font-medium text-[var(--text-primary)] flex-1 min-w-0 break-words">
                {item.name || `Товар #${item.id}`}
              </span>
              <span className="text-xs text-[var(--text-tertiary)] flex-shrink-0 mt-0.5">
                {item.reason}
              </span>
            </li>
          ))}
        </ul>

        {cartIsEmpty && (
          <p className="mb-4 text-sm text-[var(--error)]">
            Корзина пуста — выберите другие товары для заказа.
          </p>
        )}
        {!cartIsEmpty && belowMinimum && (
          <p className="mb-4 text-sm text-[var(--warning)]">
            Сумма заказа стала ниже минимальной — добавьте товаров перед оплатой.
          </p>
        )}

        <DialogFooter>
          <button
            type="button"
            onClick={onBackToCart}
            className={cn(
              'inline-flex items-center justify-center gap-2 px-4 py-3 rounded-card text-sm font-medium',
              'bg-[var(--surface-secondary)] text-[var(--text-primary)]',
              'hover:bg-[var(--surface-tertiary)] transition-colors',
              'sm:flex-1',
            )}
          >
            <ArrowLeft weight="bold" className="w-4 h-4" />
            {canContinue ? 'Заменить товары' : 'Вернуться в корзину'}
          </button>
          {canContinue && (
            <button
              type="button"
              onClick={onContinue}
              className={cn(
                'inline-flex items-center justify-center gap-2 px-4 py-3 rounded-card text-sm font-medium sm:flex-1',
                'bg-[var(--primary)] text-[var(--text-inverse)]',
                'hover:opacity-90 transition-opacity',
              )}
            >
              <CreditCard weight="fill" className="w-4 h-4" />
              Продолжить оплату
            </button>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
